import React, { useState, useEffect } from 'react';
import { intercompanyApi, settingsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './Intercompany.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function getCurrentMonthYear() {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

export default function Intercompany() {
  const { month: currentMonth, year: currentYear } = getCurrentMonthYear();
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [billing, setBilling] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [manualEntries, setManualEntries] = useState({
    payroll: '',
    farm_products: '',
    payroll_notes: '',
    farm_products_notes: '',
  });
  const [mileageEntries, setMileageEntries] = useState([]);

  useEffect(() => {
    async function loadSettings() {
      try {
        const data = await settingsApi.getAll();
        setSettings(data.settings);
      } catch (e) {
        setError(e.message);
      }
    }
    loadSettings();
  }, []);

  async function loadBilling() {
    setLoading(true);
    setError('');
    try {
      const data = await intercompanyApi.calculate({ month, year });
      setBilling(data);
      if (data.mileage_markets) {
        setMileageEntries(data.mileage_markets.map(m => ({
          market_id: m.market_id,
          market_name: m.market_name,
          miles: m.miles || '',
        })));
      }
      if (data.manual) {
        setManualEntries({
          payroll: data.manual.payroll || '',
          farm_products: data.manual.farm_products || '',
          payroll_notes: data.manual.payroll_notes || '',
          farm_products_notes: data.manual.farm_products_notes || '',
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(val) {
    const n = parseFloat(val || 0);
    return isNaN(n) ? '$0.00' : `$${n.toFixed(2)}`;
  }

  // AR owes CC totals
  const ccProductsTotal = billing?.cc_products?.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0) || 0;
  const kegTotal = billing?.keg_charges?.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0) || 0;
  const arOwesCC = ccProductsTotal + kegTotal;

  // CC owes AR totals
  const mileageTotal = mileageEntries.reduce((sum, m) => {
    const rate = parseFloat(settings?.mileage_rate || 0.67);
    return sum + (parseFloat(m.miles || 0) * rate);
  }, 0);
  const payrollTotal = parseFloat(manualEntries.payroll || 0);
  const farmProductsTotal = parseFloat(manualEntries.farm_products || 0);
  const ingredientChargesTotal = billing?.ingredient_charges?.reduce((sum, r) => sum + parseFloat(r.charge || 0), 0) || 0;
  const ccPayrollTotal = parseFloat(billing?.cc_payroll || 0);
  const ccOwesAR = mileageTotal + payrollTotal + farmProductsTotal + ingredientChargesTotal + ccPayrollTotal;

  const netOwed = arOwesCC - ccOwesAR;

  async function handleSave() {
    setSaving(true);
    try {
      await intercompanyApi.save({
        month, year,
        ar_owes_cc: arOwesCC,
        cc_owes_ar: ccOwesAR,
        net: netOwed,
        mileage_entries: mileageEntries,
        manual_entries: manualEntries,
      });
      alert('Billing record saved!');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <AdminPageHeader
        title="Intercompany Billing"
        subtitle="Monthly settlement between Apple Ridge Farm and Collective Culture"
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        <div className="billing-controls">
          <div className="field-row" style={{maxWidth: 400}}>
            <div className="field">
              <label>Month</label>
              <select className="market-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Year</label>
              <select className="market-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <button className="btn-primary" onClick={loadBilling} disabled={loading}>
            {loading ? 'Calculating...' : 'Calculate'}
          </button>
        </div>

        {billing && (
          <div className="billing-grid">

            {/* AR owes CC */}
            <div className="billing-card">
              <div className="billing-card-header ar-owes">
                <h3>AR owes CC</h3>
                <span className="billing-total">{formatCurrency(arOwesCC)}</span>
              </div>

              <div className="billing-section">
                <h4>CC Products Sold at AR Markets</h4>
                {billing.cc_products?.length === 0 ? (
                  <p className="billing-empty">No CC product sales recorded for this period.</p>
                ) : (
                  <table className="billing-table">
                    <thead>
                      <tr><th>Market</th><th>Product</th><th>Qty</th><th>Unit Price</th><th>Amount</th></tr>
                    </thead>
                    <tbody>
                      {billing.cc_products?.map((r, i) => (
                        <tr key={i}>
                          <td>{r.market_name}</td>
                          <td>{r.product_name}</td>
                          <td>{r.qty_sold}</td>
                          <td>{formatCurrency(r.unit_price)}</td>
                          <td className="td-price">{formatCurrency(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="4"><strong>Subtotal</strong></td><td className="td-price"><strong>{formatCurrency(ccProductsTotal)}</strong></td></tr>
                    </tfoot>
                  </table>
                )}
              </div>

              <div className="billing-section">
                <h4>Keg Volume at AR Markets</h4>
                {billing.keg_charges?.length === 0 ? (
                  <p className="billing-empty">No keg records for this period.</p>
                ) : (
                  <table className="billing-table">
                    <thead>
                      <tr><th>Market</th><th>Flavor</th><th>Lbs Used</th><th>Rate</th><th>Amount</th></tr>
                    </thead>
                    <tbody>
                      {billing.keg_charges?.map((r, i) => (
                        <tr key={i}>
                          <td>{r.market_name}</td>
                          <td>{r.flavor}</td>
                          <td>{r.weight_used}</td>
                          <td>{formatCurrency(settings?.keg_billing_rate)}/lb</td>
                          <td className="td-price">{formatCurrency(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="4"><strong>Subtotal</strong></td><td className="td-price"><strong>{formatCurrency(kegTotal)}</strong></td></tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </div>

            {/* CC owes AR */}
            <div className="billing-card">
              <div className="billing-card-header cc-owes">
                <h3>CC owes AR</h3>
                <span className="billing-total">{formatCurrency(ccOwesAR)}</span>
              </div>

              <div className="billing-section">
                <h4>Mileage Reimbursement</h4>
                <p className="billing-hint">Rate: {formatCurrency(settings?.mileage_rate)}/mile</p>
                <table className="billing-table">
                  <thead>
                    <tr><th>Market</th><th>Miles</th><th>Amount</th></tr>
                  </thead>
                  <tbody>
                    {mileageEntries.map((m, i) => (
                      <tr key={i}>
                        <td>{m.market_name}</td>
                        <td>
                          <input type="number" className="qty-input" value={m.miles}
                            onChange={e => setMileageEntries(ms => ms.map((me, idx) => idx === i ? { ...me, miles: e.target.value } : me))}
                            min="0" placeholder="0"
                          />
                        </td>
                        <td className="td-price">{formatCurrency(parseFloat(m.miles || 0) * parseFloat(settings?.mileage_rate || 0.67))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan="2"><strong>Subtotal</strong></td><td className="td-price"><strong>{formatCurrency(mileageTotal)}</strong></td></tr>
                  </tfoot>
                </table>
              </div>

              <div className="billing-section">
                <h4>Payroll (CC portion)</h4>
                <div className="manual-entry">
                  <div className="price-input-wrap">
                    <span className="price-prefix">$</span>
                    <input type="number" step="0.01" className="price-input"
                      value={manualEntries.payroll}
                      onChange={e => setManualEntries(m => ({ ...m, payroll: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <input type="text" className="notes-input"
                    value={manualEntries.payroll_notes}
                    onChange={e => setManualEntries(m => ({ ...m, payroll_notes: e.target.value }))}
                    placeholder="Notes (e.g. from ADP report)..."
                  />
                </div>
              </div>

              <div className="billing-section">
                <h4>🌱 AR Ingredients (from Production Log)</h4>
                {!billing.ingredient_charges || billing.ingredient_charges.length === 0 ? (
                  <p className="billing-empty">No AR ingredient usage recorded this period.</p>
                ) : (
                  <table className="billing-table">
                    <thead>
                      <tr><th>Ingredient</th><th>Amount Used</th><th>Price/Unit</th><th>Charge</th></tr>
                    </thead>
                    <tbody>
                      {billing.ingredient_charges.map((r, i) => (
                        <tr key={i}>
                          <td>{r.ingredient}</td>
                          <td>{r.total_amount} {r.unit}</td>
                          <td>{formatCurrency(r.price_per_unit)}/{r.unit}</td>
                          <td className="td-price">{r.price_per_unit === '0.00' ? <span style={{color:'#e67e22'}}>No price set</span> : formatCurrency(r.charge)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr><td colSpan="3"><strong>Subtotal</strong></td><td className="td-price"><strong>{formatCurrency(ingredientChargesTotal)}</strong></td></tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {ccPayrollTotal > 0 && (
                <div className="billing-section">
                  <h4>💰 CC Payroll (ADP)</h4>
                  <table className="billing-table">
                    <thead><tr><th>Description</th><th>Amount</th></tr></thead>
                    <tbody>
                      <tr><td>CC Staff ADP Payroll — {billing.month}/{billing.year}</td><td className="td-price">{formatCurrency(ccPayrollTotal)}</td></tr>
                    </tbody>
                    <tfoot>
                      <tr><td><strong>Subtotal</strong></td><td className="td-price"><strong>{formatCurrency(ccPayrollTotal)}</strong></td></tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <div className="billing-section">
                <h4>Farm Products (manual catch-all)</h4>
                <div className="manual-entry">
                  <div className="price-input-wrap">
                    <span className="price-prefix">$</span>
                    <input type="number" step="0.01" className="price-input"
                      value={manualEntries.farm_products}
                      onChange={e => setManualEntries(m => ({ ...m, farm_products: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <input type="text" className="notes-input"
                    value={manualEntries.farm_products_notes}
                    onChange={e => setManualEntries(m => ({ ...m, farm_products_notes: e.target.value }))}
                    placeholder="Notes (e.g. berries, herbs used in CC products)..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {billing && (
          <div className="billing-summary">
            <div className={`net-amount ${netOwed >= 0 ? 'ar-owes' : 'cc-owes'}`}>
              <span className="net-label">
                {netOwed >= 0 ? 'Net: AR owes CC' : 'Net: CC owes AR'}
              </span>
              <span className="net-value">{formatCurrency(Math.abs(netOwed))}</span>
            </div>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Billing Record'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
